import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurant, useCategories, useMenuItems, useOrders } from "@/hooks/useRestaurant";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogOut, UtensilsCrossed, ClipboardList, Palette, LayoutDashboard, Store } from "lucide-react";
import AdminOverview from "./AdminOverview";
import AdminMenu from "./AdminMenu";
import AdminOrders from "./AdminOrders";
import AdminBranding from "./AdminBranding";
import AdminBranches from "./AdminBranches";

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const { restaurant, loading: restLoading, updateRestaurant } = useRestaurant();
  const { categories, addCategory, deleteCategory, refetch: refetchCategories } = useCategories(restaurant?.id);
  const { items: menuItems, addItem, updateItem, deleteItem, refetch: refetchMenu } = useMenuItems(restaurant?.id);
  const { orders, updateOrderStatus, refetch: refetchOrders } = useOrders(restaurant?.id);

  if (restLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b bg-card px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
            {restaurant?.logo_type === "emoji" ? restaurant.logo_emoji : restaurant?.name?.charAt(0)}
          </div>
          <div>
            <h1 className="font-bold text-foreground">{restaurant?.name || "My Restaurant"}</h1>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={signOut}>
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      {/* Main content */}
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="gap-2">
              <LayoutDashboard className="w-4 h-4 hidden sm:block" /> Overview
            </TabsTrigger>
            <TabsTrigger value="menu" className="gap-2">
              <UtensilsCrossed className="w-4 h-4 hidden sm:block" /> Menu
            </TabsTrigger>
            <TabsTrigger value="branches" className="gap-2">
              <Store className="w-4 h-4 hidden sm:block" /> Branches
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <ClipboardList className="w-4 h-4 hidden sm:block" /> Orders
            </TabsTrigger>
            <TabsTrigger value="branding" className="gap-2">
              <Palette className="w-4 h-4 hidden sm:block" /> Branding
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <AdminOverview orders={orders} menuItems={menuItems} restaurant={restaurant} />
          </TabsContent>

          <TabsContent value="menu">
            <AdminMenu
              categories={categories}
              menuItems={menuItems}
              restaurantId={restaurant?.id}
              onAddCategory={addCategory}
              onDeleteCategory={deleteCategory}
              onAddItem={addItem}
              onUpdateItem={updateItem}
              onDeleteItem={deleteItem}
            />
          </TabsContent>

          <TabsContent value="branches">
            <AdminBranches restaurantId={restaurant?.id} menuItems={menuItems} />
          </TabsContent>

          <TabsContent value="orders">
            <AdminOrders orders={orders} onUpdateStatus={updateOrderStatus} onRefresh={refetchOrders} />
          </TabsContent>

          <TabsContent value="branding">
            <AdminBranding restaurant={restaurant} onUpdate={updateRestaurant} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
